import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { getUserData } from "../../services/apiService";
import {
  getUserSupportTickets,
  getSupportTicketDetails,
  addSupportTicketComment,
  uploadSupportTicketAttachment,
  closeSupportTicket,
} from "../../services/clickupService";
import PageBreadcrumb from "../../components/common/PageBreadcrumb/PageBreadcrumb";
import DataTable from "../common/dataTable";
import "./SupportTickets.scss";

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Support flow interaction state
  const [newCommentText, setNewCommentText] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [addCommentError, setAddCommentError] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [rating, setRating] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const [closeError, setCloseError] = useState("");

  // List filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedStatusFilter, setAppliedStatusFilter] = useState("all");
  const [appliedReasonFilter, setAppliedReasonFilter] = useState("all");
  const [appliedModuleFilter, setAppliedModuleFilter] = useState("all");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);

  const loadTickets = async ({ isManualRefresh = false } = {}) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError("");

      const user = getUserData();
      const email = user?.EmailId;

      if (!email) {
        setError("Unable to load tickets: user email not available.");
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const data = await getUserSupportTickets(email);
      setTickets(data || []);
    } catch (err) {
      console.error("Error loading support tickets:", err);
      setError(
        err.message ||
          "We couldn't load your support tickets right now. Please try again later."
      );
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    loadTickets({ isManualRefresh: true });
  };

  const handleOpenDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsError("");
    setTicketDetails(null);

    try {
      const details = await getSupportTicketDetails(ticket.id);
      if (!details) {
        setDetailsError(
          "We couldn't load the full details for this ticket. Please try again later."
        );
      } else {
        setTicketDetails(details);
      }
    } catch (err) {
      console.error("Error loading ticket details:", err);
      setDetailsError(
        err.message ||
          "We couldn't load the full details for this ticket. Please try again later."
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedTicket(null);
    setTicketDetails(null);
    setDetailsError("");
    setNewCommentText("");
    setAddCommentError("");
    setSelectedFiles([]);
    setUploadError("");
    setShowFeedbackModal(false);
    setRating(null);
    setFeedbackText("");
    setCloseError("");
  };

  const handleAddComment = async () => {
    if (!ticketDetails || !selectedTicket) return;
    const text = newCommentText.trim();
    if (!text) {
      setAddCommentError("Please enter a comment before sending.");
      return;
    }

    try {
      setIsAddingComment(true);
      setAddCommentError("");

      // Get current user data and format message for ClickUp as "Name : message"
      const user = getUserData();
      const displayName = user?.UserName || user?.EmailId || "Unknown user";
      const clickUpMessage = `${displayName} : ${text}`;

      const newComment = await addSupportTicketComment(
        selectedTicket.id,
        clickUpMessage,
        user
      );

      // For UI, show only the original message text without the name prefix
      const uiComment = {
        ...newComment,
        text,
      };

      setTicketDetails((prev) =>
        prev
          ? {
              ...prev,
              comments: [...(prev.comments || []), uiComment],
            }
          : prev
      );
      setNewCommentText("");
    } catch (err) {
      console.error("Error adding comment:", err);
      setAddCommentError(
        err.message ||
          "We couldn't add your comment right now. Please try again later."
      );
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setUploadError("");
  };

  const handleUploadAttachments = async () => {
    if (!ticketDetails || !selectedTicket) return;
    if (!selectedFiles || selectedFiles.length === 0) {
      setUploadError("Please choose at least one file to upload.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadError("");

      const uploads = selectedFiles.map((file) =>
        uploadSupportTicketAttachment(selectedTicket.id, file)
      );

      await Promise.all(uploads);

      // Refresh details so new attachments appear under Description
      const refreshedDetails = await getSupportTicketDetails(selectedTicket.id);
      if (refreshedDetails) {
        setTicketDetails(refreshedDetails);
      }

      setSelectedFiles([]);
    } catch (err) {
      console.error("Error uploading attachments:", err);
      setUploadError(
        err.message ||
          "We couldn't upload your files right now. Please try again later."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenFeedbackModal = () => {
    setShowFeedbackModal(true);
    setCloseError("");
    setRating(null);
    setFeedbackText("");
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setCloseError("");
    setRating(null);
    setFeedbackText("");
  };

  const handleSubmitCloseCase = async () => {
    if (!ticketDetails || !selectedTicket) return;

    if (rating !== null && (rating < 1 || rating > 5)) {
      setCloseError("Rating must be between 1 and 5.");
      return;
    }

    try {
      setIsClosing(true);
      setCloseError("");

      const result = await closeSupportTicket(selectedTicket.id, {
        rating,
        feedback: feedbackText,
      });

      const newStatus = result.status?.status || result.status || "Closed";

      // Update details view
      setTicketDetails((prev) =>
        prev
          ? {
              ...prev,
              status: newStatus,
            }
          : prev
      );

      // Update list view
      setTickets((prevTickets) =>
        prevTickets.map((t) =>
          t.id === selectedTicket.id ? { ...t, status: newStatus } : t
        )
      );

      // Close feedback modal and refresh ticket details
      setShowFeedbackModal(false);
      setRating(null);
      setFeedbackText("");
      
      // Refresh ticket details to show updated status
      const refreshedDetails = await getSupportTicketDetails(selectedTicket.id);
      if (refreshedDetails) {
        setTicketDetails(refreshedDetails);
      }
    } catch (err) {
      console.error("Error closing support ticket:", err);
      setCloseError(
        err.message ||
          "We couldn't close this ticket right now. Please try again later."
      );
    } finally {
      setIsClosing(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const date =
        typeof value === "number"
          ? new Date(value)
          : new Date(parseInt(value, 10));
      return format(date, "dd/MM/yyyy HH:mm");
    } catch {
      return "-";
    }
  };

  // Build distinct options from tickets
  const statusOptions = Array.from(
    new Set((tickets || []).map((t) => t.status || "Unknown"))
  ).sort();
  const reasonOptions = Array.from(
    new Set(
      (tickets || [])
        .map((t) => t.reason || null)
        .filter((v) => v && String(v).trim().length > 0)
    )
  ).sort();
  const moduleOptions = Array.from(
    new Set(
      (tickets || [])
        .map((t) => t.module || null)
        .filter((v) => v && String(v).trim().length > 0)
    )
  ).sort();

  // Apply filters
  const filteredTickets = (tickets || []).filter((ticket) => {
    const statusValue = ticket.status || "Unknown";
    const reasonValue = ticket.reason || null;
    const moduleValue = ticket.module || null;

    // Status filter
    if (appliedStatusFilter !== "all" && statusValue !== appliedStatusFilter) {
      return false;
    }

    // Reason filter
    if (
      appliedReasonFilter !== "all" &&
      String(reasonValue || "").trim() !== appliedReasonFilter
    ) {
      return false;
    }

    // Module filter
    if (
      appliedModuleFilter !== "all" &&
      String(moduleValue || "").trim() !== appliedModuleFilter
    ) {
      return false;
    }

    // Date range filter
    const createdTs = Number(ticket.createdAt) || 0;
    if (appliedFromDate) {
      const fromTs = new Date(`${appliedFromDate}T00:00:00`).getTime();
      if (createdTs < fromTs) return false;
    }
    if (appliedToDate) {
      const toTs = new Date(`${appliedToDate}T23:59:59`).getTime();
      if (createdTs > toTs) return false;
    }

    return true;
  });

  // Transform tickets data to include formatted date for DataTable
  const transformedTickets = filteredTickets.map((ticket) => ({
    ...ticket,
    Created: formatDate(ticket.createdAt),
    Status: ticket.status || "Unknown",
  }));

  // Define columns for DataTable
  const columns = [
    { label: "Subject", accessor: "name" },
    { label: "Reason", accessor: "reason" },
    { label: "Module", accessor: "module" },
    { label: "Status", accessor: "Status" },
    { label: "Created", accessor: "Created" },
  ];

  // Define actions for DataTable
  const actions = [
    {
      label: "View Details",
      onClick: handleOpenDetails,
      className: "btn-info btn-sm",
    },
  ];

  return (
    <div className="user-mgmt-page support-tickets-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/dashboard" },
          { label: "My Support Tickets" },
        ]}
        actions={[
          {
            icon: "fas fa-filter",
            label: "Filters",
            onClick: () => {
              // When opening modal, start from currently applied filters
              setStatusFilter(appliedStatusFilter);
               setReasonFilter(appliedReasonFilter);
               setModuleFilter(appliedModuleFilter);
              setFromDate(appliedFromDate);
              setToDate(appliedToDate);
              setShowFilterModal(true);
            },
          },
          {
            icon: "fas fa-refresh",
            label: isRefreshing ? "Refreshing..." : "Refresh",
            onClick: handleRefresh,
            disabled: loading || isRefreshing,
          },
        ]}
      />

      <div className="container-fluid mt-3">

      {loading && (
        <div className="text-muted small">Loading your tickets...</div>
      )}

      {error && !loading && (
        <div className="alert alert-danger py-2 small" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && tickets.length === 0 && (
        <div className="text-muted small">
          You haven&apos;t submitted any support tickets yet.
        </div>
      )}

      {!loading && tickets.length > 0 && (
        <DataTable
          data={transformedTickets}
          columns={columns}
          actions={actions}
          sortable={["name", "reason", "module", "Status", "Created"]}
          showToggle={true}
        />
      )}

      {showDetailsModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0">
                  {selectedTicket?.name || "Ticket details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseDetails}
                ></button>
              </div>
              <div className="modal-body">
                {detailsLoading && (
                  <div className="text-muted small">
                    Loading ticket details...
                  </div>
                )}

                {detailsError && !detailsLoading && (
                  <div className="alert alert-danger py-2 small" role="alert">
                    {detailsError}
                  </div>
                )}

                {ticketDetails && !detailsLoading && !detailsError && (() => {
                  const isClosedStatus =
                    ticketDetails.status &&
                    ["closed", "complete"].includes(
                      String(ticketDetails.status).toLowerCase()
                    );

                  return (
                  <div>
                    <div className="border rounded p-2 bg-light mb-3">
                      <div className="d-flex flex-wrap align-items-center justify-content-between mb-1">
                        <div className="d-flex flex-wrap gap-2 small">
                          <span className="badge bg-secondary text-uppercase">
                            {ticketDetails.status || "Unknown"}
                          </span>
                          {ticketDetails.reason && (
                            <span className="badge bg-white text-muted border">
                              {ticketDetails.reason}
                            </span>
                          )}
                          {ticketDetails.module && (
                            <span className="badge bg-white text-muted border">
                              {ticketDetails.module}
                            </span>
                          )}
                        </div>
                        <div className="small text-muted">
                          <strong>Created:</strong>{" "}
                          {formatDate(ticketDetails.createdAt)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h6 className="small fw-bold mb-2">Description</h6>
                      <div className="border rounded p-3 bg-light small support-ticket-description">
                        {ticketDetails.description &&
                        ticketDetails.description.trim().length > 0 ? (
                          ticketDetails.description
                            .split("\n")
                            .map((line, idx) => (
                              <p key={idx} className="mb-1">
                                {line}
                              </p>
                            ))
                        ) : (
                          <p className="mb-0 text-muted">No description.</p>
                        )}

                        {ticketDetails.attachments &&
                          ticketDetails.attachments.length > 0 && (
                            <div className="mt-3">
                              <h6 className="small fw-bold mb-2">
                                Attachments
                              </h6>
                              <ul className="list-unstyled mb-0 attachments-list">
                                {ticketDetails.attachments.map((file) => (
                                  <li
                                    key={file.id}
                                    className="d-flex align-items-center small mb-1"
                                  >
                                    <i className="fas fa-paperclip me-2 text-muted"></i>
                                    {file.url ? (
                                      <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {file.name}
                                      </a>
                                    ) : (
                                      <span>{file.name}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="mt-3 support-ticket-comments">
                      <h6 className="small fw-bold mb-2">Comments</h6>
                      <div
                        className="border rounded p-3 bg-light small comments-list"
                      >
                        {ticketDetails.comments &&
                        ticketDetails.comments.length > 0 ? (
                          <ul className="list-unstyled mb-0">
                            {ticketDetails.comments.map((c, idx) => (
                              <li
                                key={c.id || idx}
                                className={idx > 0 ? "mt-3 pt-3 border-top" : ""}
                              >
                                <div className="d-flex justify-content-between mb-1">
                                  <span className="fw-semibold">
                                    {c.author || "Unknown"}
                                  </span>
                                  <span className="text-muted">
                                    {formatDate(c.date)}
                                  </span>
                                </div>
                                <div>{c.text || "(no text)"}</div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted small mb-0">
                            No comments yet on this ticket.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6 className="small fw-bold mb-2">Add a comment</h6>
                      <textarea
                        className="form-control form-control-sm mb-2"
                        rows={3}
                        placeholder="Type your reply here..."
                        value={newCommentText}
                        onChange={(e) => {
                          setNewCommentText(e.target.value);
                          if (addCommentError) setAddCommentError("");
                        }}
                        disabled={isClosedStatus || isAddingComment || detailsLoading}
                      />
                      {addCommentError && (
                        <div className="text-danger small mb-2">
                          {addCommentError}
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleAddComment}
                        disabled={
                          isClosedStatus ||
                          isAddingComment ||
                          detailsLoading ||
                          !newCommentText.trim()
                        }
                      >
                        {isAddingComment ? "Sending..." : "Send Comment"}
                      </button>
                      {isClosedStatus && (
                        <p className="text-muted small mt-2 mb-0">
                          This ticket is closed. New comments can no longer be added.
                        </p>
                      )}
                    </div>

                    <div className="mt-4">
                      <h6 className="small fw-bold mb-2">
                        Upload additional information
                      </h6>
                      <div className="custom-upload-wrapper">
                        <input
                          type="file"
                          className="custom-file-input"
                          multiple
                          onChange={handleFileChange}
                          disabled={isClosedStatus || isUploading || detailsLoading}
                        />
                        <button
                          type="button"
                          className="custom-upload-btn"
                          onClick={handleUploadAttachments}
                          disabled={
                            isClosedStatus ||
                            isUploading ||
                            detailsLoading ||
                            !selectedFiles ||
                            selectedFiles.length === 0
                          }
                        >
                          {isUploading ? "Uploading..." : "Upload"}
                        </button>
                      </div>
                      {uploadError && (
                        <div className="text-danger small mt-1">
                          {uploadError}
                        </div>
                      )}
                      {isClosedStatus && (
                        <div className="text-muted small mt-1">
                          This ticket is closed. Uploads are disabled.
                        </div>
                      )}
                      {selectedFiles && selectedFiles.length > 0 && (
                        <div className="text-muted small mt-1">
                          {selectedFiles.length} file
                          {selectedFiles.length > 1 ? "s" : ""} selected
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h6 className="small fw-bold mb-0">
                         
                          {isClosedStatus ? "Ticket closed" : ""}
                        </h6>
                        {!isClosedStatus && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={handleOpenFeedbackModal}
                            disabled={isClosing || detailsLoading}
                          >
                            <i className="fas fa-lock me-1"></i>Close Ticket
                          </button>
                        )}
                      </div>

                      {isClosedStatus && (
                        <div className="alert alert-success py-2 small mb-0">
                          <i className="fas fa-check-circle me-2"></i>
                          This ticket is already closed. Thank you for your feedback.
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleCloseDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0">
                  <i className="fas fa-star me-2 text-warning"></i>
                  Close Ticket & Provide Feedback
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseFeedbackModal}
                  disabled={isClosing}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-4">
                  Please share your feedback before closing this ticket. This helps us improve our support service.
                </p>

                <div className="mb-4">
                  <label className="form-label fw-semibold mb-3 d-block">
                    <i className="fas fa-star me-2 text-warning"></i>
                    How satisfied are you with our support?
                  </label>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn ${
                          rating === value
                            ? "btn-warning text-white"
                            : "btn-outline-warning"
                        }`}
                        style={{
                          minWidth: "50px",
                          height: "50px",
                          borderRadius: "10px",
                          fontSize: "1.2rem",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => setRating(value)}
                        disabled={isClosing}
                        onMouseEnter={(e) => {
                          if (rating !== value && !isClosing) {
                            e.currentTarget.style.transform = "scale(1.15)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <i className="fas fa-star"></i>
                      </button>
                    ))}
                    {rating && (
                      <span className="ms-2 text-muted">
                        <strong>({rating}/5)</strong>
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold mb-2 d-block">
                    <i className="fas fa-comment me-2 text-muted"></i>
                    Additional Comments <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    placeholder="Tell us about your experience with this support ticket..."
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    disabled={isClosing}
                    style={{
                      resize: "vertical",
                    }}
                  />
                </div>

                {closeError && (
                  <div className="alert alert-danger py-2 small mb-0">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {closeError}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleCloseFeedbackModal}
                  disabled={isClosing}
                >
                  <i className="fas fa-times me-1"></i>Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleSubmitCloseCase}
                  disabled={isClosing || !rating}
                >
                  {isClosing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Closing Ticket...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check me-1"></i>Submit & Close Ticket
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0">
                  <i className="fas fa-filter me-2 text-muted"></i>
                  Filter Tickets
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setShowFilterModal(false)}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label form-label-sm mb-1">
                    Status
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    disabled={loading}
                  >
                    <option value="all">Select Status</option>
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label form-label-sm mb-1">
                    Reason
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    disabled={loading || reasonOptions.length === 0}
                  >
                    <option value="all">Select Reason</option>
                    {reasonOptions.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label form-label-sm mb-1">
                    Module
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                    disabled={loading || moduleOptions.length === 0}
                  >
                    <option value="all">Select Module</option>
                    {moduleOptions.map((mod) => (
                      <option key={mod} value={mod}>
                        {mod}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="row g-2">
                  <div className="col-sm-6">
                    <label className="form-label form-label-sm mb-1">
                      From date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="col-sm-6">
                    <label className="form-label form-label-sm mb-1">
                      To date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setStatusFilter("all");
                    setReasonFilter("all");
                    setModuleFilter("all");
                    setFromDate("");
                    setToDate("");
                  }}
                  disabled={loading}
                >
                  Clear filters
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    // Commit current modal values as active filters
                    setAppliedStatusFilter(statusFilter);
                    setAppliedReasonFilter(reasonFilter);
                    setAppliedModuleFilter(moduleFilter);
                    setAppliedFromDate(fromDate);
                    setAppliedToDate(toDate);
                    setShowFilterModal(false);
                  }}
                  disabled={loading}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SupportTickets;

